/**
 * Opens a Blob in a new browser tab, keeping the object URL alive until the
 * tab is actually done with it.
 *
 * The naive version of this — `window.open(url)` followed by
 * `setTimeout(() => URL.revokeObjectURL(url), 100)` — is a race. The new tab
 * fetches the blob: URL asynchronously, and if the revoke wins, the fetch
 * fails and the tab renders the browser's own network-error page ("check your
 * internet connection") instead of the document. Larger payloads lose the race
 * more often, which is why the bug looks intermittent.
 *
 * Here the URL is revoked when the opened tab closes, with a hard cap so a tab
 * left open forever doesn't leak the blob for the lifetime of the session.
 */

/** How often to check whether the opened tab has been closed. */
const CLOSE_POLL_INTERVAL_MS = 1000;

/** Upper bound on how long an object URL is kept alive. */
const MAX_LIFETIME_MS = 10 * 60 * 1000;

/** Grace period before revoking a URL handed to a download link. */
const DOWNLOAD_REVOKE_DELAY_MS = 60 * 1000;

export function openBlobInNewTab(blob: Blob, fallbackFilename: string): void {
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank');

  if (!tab) {
    // The popup was blocked. `window.open` runs from an HTTP response handler,
    // so the browser no longer counts it as user-initiated. Rather than fail
    // silently, save the file instead.
    downloadBlobUrl(url, fallbackFilename);
    return;
  }

  let poll: ReturnType<typeof setInterval> | undefined;

  const release = () => {
    if (poll !== undefined) {
      clearInterval(poll);
      poll = undefined;
    }
    URL.revokeObjectURL(url);
  };

  poll = setInterval(() => {
    if (tab.closed) {
      release();
    }
  }, CLOSE_POLL_INTERVAL_MS);

  setTimeout(release, MAX_LIFETIME_MS);
}

/** Saves a Blob as a file with the given name. */
export function downloadBlob(blob: Blob, filename: string): void {
  downloadBlobUrl(URL.createObjectURL(blob), filename);
}

function downloadBlobUrl(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();

  // The download is started synchronously by the click, but the browser reads
  // the blob afterwards, so the URL cannot be revoked immediately.
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_DELAY_MS);
}
